import { useState, useCallback, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { FilterIcon, XIcon } from "lucide-react";
import FilterField from "./FilterField";
import { Separator } from "@/components/ui/separator";
import { buildFilterObject, FilterColumn, parseFilterFromUrl } from ".";
import { useSearchParams } from "react-router-dom";

interface FilterMenuProps<T> {
    columns: FilterColumn<T>[];
    disableFilter?: boolean;
}

export default function FilterMenu<T>({
    columns,
    disableFilter = false,
}: FilterMenuProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Parse initial filters from URL
    const initialFilters = useMemo(
        () => parseFilterFromUrl(searchParams.get("filter")),
        [],
    );

    const [localFilters, setLocalFilters] =
        useState<Record<string, string>>(initialFilters);

    const handleFieldChange = useCallback((fieldId: string, value: string) => {
        setLocalFilters((prev) => ({
            ...prev,
            [fieldId]: value,
        }));
    }, []);

    const handleApply = useCallback(() => {
        const params = new URLSearchParams(searchParams);
        const filterObj = buildFilterObject(localFilters);

        if (Object.keys(filterObj).length > 0) {
            params.set("filter", JSON.stringify(filterObj));
        } else {
            params.delete("filter");
        }
        params.delete("skip");
        setSearchParams(params);
        setIsOpen(false);
    }, [localFilters, searchParams, setSearchParams]);

    const handleCancel = useCallback(() => {
        setLocalFilters(initialFilters);
        setIsOpen(false);
    }, [initialFilters]);

    const handleClear = useCallback(() => {
        setLocalFilters({});
        const params = new URLSearchParams(searchParams);
        params.delete("filter");
        params.delete("skip");
        setSearchParams(params);
        setIsOpen(false);
    }, [searchParams, setSearchParams]);

    return (
        <>
            <div>
                <Button
                    disabled={disableFilter}
                    variant={"outline"}
                    onClick={() => setIsOpen(true)}
                >
                    <FilterIcon />
                </Button>
            </div>
            <div
                className={`fixed right-0 top-0 z-50 h-full min-w-[400px] max-w-[800px] transform rounded-xl border-l-2 border-primary bg-white p-2 shadow-lg transition-transform duration-300 ${
                    isOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between p-2">
                    <h1 className="p-[4px] text-xl font-bold">Filter</h1>
                    <button onClick={handleCancel}>
                        <XIcon />
                    </button>
                </div>
                <Separator className="absolute left-0 bg-primary" />
                {isOpen && (
                    <div className="flex h-[88%] flex-col gap-4 overflow-y-scroll bg-slate-50 px-5 py-7">
                        {columns?.map((col) => {
                            const fieldId = String(col?.filterId || col.id);
                            return (
                                <FilterField<T>
                                    key={fieldId}
                                    column={col}
                                    value={localFilters[fieldId] ?? ""}
                                    label={col.headingText}
                                    onChange={(value) =>
                                        handleFieldChange(fieldId, value)
                                    }
                                />
                            );
                        })}
                    </div>
                )}

                <Separator className="absolute left-0 bg-primary" />
                <div className="flex items-center gap-4 py-4">
                    <Button
                        onClick={handleClear}
                        className="w-full"
                        variant="outline"
                    >
                        Clear
                    </Button>
                    <Button onClick={handleApply} className="w-full">
                        Apply
                    </Button>
                </div>
            </div>
        </>
    );
}
